import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Alert } from '../../components/ui/alert';
import { useAuthStore } from '../../stores/authStore';
import { Loader2 } from 'lucide-react';
import { PageHeader } from '../../components/common';
import { api } from '../../services/api';
import { showToast } from '../../stores/uiStore';
import { getOperationErrorMessage } from '../../config/errorMessages';
import { usePageTitle } from '../../hooks/usePageTitle';

const CATEGORIES = [
  '機能・使い方について',
  '料金・プランについて',
  '不具合・エラー報告',
  'その他',
];

export const ContactPage: React.FC = () => {
  usePageTitle('お問い合わせ');

  const { user } = useAuthStore();

  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const isDisabled = !category || !message || loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/contact', { name: user?.name || '', email: user?.email || '', category, message });
      setSuccess(true);
      setCategory('');
      setMessage('');
    } catch (err: any) {
      if (err?.response?.status === 429) {
        showToast.error('送信制限に達しました。1時間後に再度お試しください');
      } else {
        showToast.error(getOperationErrorMessage('submit'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader title="お問い合わせ" />

      <p className="text-sm text-gray-500 mb-6">
        ご不明な点やご要望がございましたら、お気軽にお問い合わせください。
      </p>

      <div className="bg-white border border-[#E2E8F0] rounded-[12px] p-7 max-w-2xl">
        {success && (
          <Alert variant="success" className="mb-6" data-testid="contact-success-message">
            お問い合わせを受け付けました。担当者よりご連絡いたします。
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
          <div>
            <label className="block text-sm font-bold text-[#0F172A] mb-2">お名前</label>
            <p data-testid="contact-name-display" className="text-sm text-[#0F172A]">{user?.name || '未設定'}</p>
          </div>

          <div>
            <label className="block text-sm font-bold text-[#0F172A] mb-2">メールアドレス</label>
            <p data-testid="contact-email-display" className="text-sm text-[#0F172A]">{user?.email || '未設定'}</p>
          </div>

          <div>
            <label className="block text-sm font-bold text-[#0F172A] mb-2">お問い合わせ種別</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="contact-category-select"
            >
              <option value="">選択してください</option>
              {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-[#0F172A] mb-2">お問い合わせ内容</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              placeholder="チャットボットの学習データを更新したいのですが、手順を教えていただけますか。"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
              data-testid="contact-message-input"
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={isDisabled} data-testid="contact-submit-button">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />送信中...</> : '送信する'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
